/**
 * Central logout / session-end cache purge (audit F-LEAK-023..027, F-LEAK-051, F-LEAK-052).
 * Clears tenant-scoped localStorage keys so the next user on a shared device
 * cannot read another tenant's cached business data.
 */

import { clearImageCaches } from '../utils/imageLoader';
import { clearServiceWorkerImageCache } from './serviceWorker';
import { clearVerification } from './security/browserVerification';
import { clearOfflineQueue } from './apiClient';

const TENANT_SCOPED_EXACT_KEYS = [
  'app_data_cache',
  'offline_queue',
  'app_logs',
  'error_log',
  'invfu_failed_image_urls',
  'system_backups',
  'last_auto_backup',
] as const;

const TENANT_SCOPED_PREFIXES = [
  'b2b-cart-state:',
  'phomemo_settings_',
  'offline_queue:',
  'app_logs:',
  'error_log:',
  'app_data_cache:',
  'system_backups:',
  'last_auto_backup:',
] as const;

export function clearSessionCaches(): void {
  if (typeof localStorage === 'undefined') return;

  for (const key of TENANT_SCOPED_EXACT_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }

  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (TENANT_SCOPED_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  } catch {
    // ignore
  }

  try {
    clearOfflineQueue();
  } catch {
    // ignore
  }

  try {
    clearImageCaches();
  } catch {
    // ignore
  }

  void clearServiceWorkerImageCache().catch(() => undefined);

  try {
    clearVerification();
  } catch {
    // ignore
  }
}
